"use client";

import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { FreeMode, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";

interface PropertyGalleryProps {
  images: string[];
  alt?: string;
}

export default function PropertyGallery({
  images,
  alt = "عقار",
}: PropertyGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // ✅ Guard BEFORE any access to images[x]

  const mainImage = images[activeIndex];

  const prev = useCallback(
    () => setActiveIndex((i) => (i === 0 ? images.length - 1 : i - 1)),
    [images.length],
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i === images.length - 1 ? 0 : i + 1)),
    [images.length],
  );

  // Keyboard + body scroll lock
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") prev();
      if (e.key === "ArrowLeft") next();
      if (e.key === "Escape") setLightboxOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxOpen, prev, next]);

  if (!images || images.length === 0) return null;

  return (
    <>
      <div className="flex flex-col gap-4" dir="rtl">
        {/* ─── Main Display ─── */}
        <div
          className="relative lg:h-140 group aspect-4/3 md:aspect-video w-full max-h-150 rounded-3xl overflow-hidden cursor-zoom-in "
          onClick={() => setLightboxOpen(true)}
        >
          <Image
            src={mainImage}
            alt={`${alt} - صورة ${activeIndex + 1}`}
            fill
            priority
            quality={85}
            className="transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 85vw, 75vw"
          />

          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-linear-to-t from-black/35 via-transparent to-black/5 pointer-events-none" />

          {/* Expand hint */}
          <div className="absolute top-4 left-4 bg-white/15 backdrop-blur-xl border border-white/20 text-white p-2.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
            <Maximize2 className="w-5 h-5" />
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="الصورة السابقة"
                className="p-2.5 rounded-xl bg-white/90 shadow-lg text-neutral-800 pointer-events-auto hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="الصورة التالية"
                className="p-2.5 rounded-xl bg-white/90 shadow-lg text-neutral-800 pointer-events-auto hover:bg-white hover:scale-110 active:scale-95 transition-all duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Counter badge */}
          <div className="absolute bottom-5 right-5 bg-black/50 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-sm font-semibold tabular-nums tracking-wide">
            {activeIndex + 1} / {images.length}
          </div>

          {/* Progress bar */}
          {images.length > 1 && (
            <div className="absolute bottom-0 inset-x-0 flex gap-0.5 px-3 pb-1.5">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`h-0.75 flex-1 rounded-full transition-all duration-500 ${
                    i === activeIndex
                      ? "bg-white"
                      : i < activeIndex
                        ? "bg-white/50"
                        : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* ─── Thumbnails ─── */}
        {images.length > 1 && (
          <Swiper
            modules={[FreeMode, Thumbs]}
            spaceBetween={12}
            slidesPerView="auto"
            freeMode
            watchSlidesProgress
            dir="rtl"
            className="w-full pb-1!"
          >
            {images.map((img, index) => (
              <SwiperSlide key={index} style={{ width: "auto" }}>
                <button
                  onClick={() => setActiveIndex(index)}
                  className={`relative w-24 h-18 md:w-28 md:h-20 rounded-xl overflow-hidden transition-all duration-300
                    ${
                      index === activeIndex
                        ? "ring-[2.5px] ring-amber-500 ring-offset-[3px] ring-offset-white opacity-100"
                        : "opacity-50 hover:opacity-90 border border-neutral-200"
                    }`}
                >
                  <Image
                    src={img}
                    alt={`${alt} - صورة مصغرة ${index + 1}`}
                    fill
                    className="object-cover"
                    sizes="112px"
                    quality={60}
                  />
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      {/* ─── Lightbox ─── */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-100 bg-black/95 backdrop-blur-2xl flex flex-col items-center justify-center"
          dir="rtl"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Top bar */}
          <div className="absolute top-5 inset-x-5 flex justify-between items-center z-10">
            <div className="text-white/70 text-sm font-medium bg-white/10 backdrop-blur-md px-4 py-2 rounded-full tabular-nums">
              {activeIndex + 1} / {images.length}
            </div>
            <button
              onClick={() => setLightboxOpen(false)}
              aria-label="إغلاق"
              className="p-2.5 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/10 hover:bg-white/20 transition-all duration-300 hover:rotate-90"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main image */}
          <div
            className="relative w-full flex-1 max-w-5xl mx-4 my-20"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={images[activeIndex]}
              alt={`${alt} - صورة ${activeIndex + 1}`}
              fill
              className="object-contain"
              sizes="100vw"
              quality={90}
              priority
            />
          </div>

          {/* Lightbox arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="الصورة السابقة"
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 p-3 text-white bg-white/5 hover:bg-white/15 rounded-full transition-all duration-300 hover:scale-110"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="الصورة التالية"
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 p-3 text-white bg-white/5 hover:bg-white/15 rounded-full transition-all duration-300 hover:scale-110"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
            </>
          )}

          {/* Bottom thumbnails */}
          {images.length > 1 && (
            <div
              className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 bg-black/40 backdrop-blur-xl rounded-2xl p-2.5 max-w-[90vw] overflow-x-auto"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`relative shrink-0 w-14 h-10 rounded-lg overflow-hidden transition-all duration-300
                    ${
                      i === activeIndex
                        ? "ring-2 ring-white opacity-100 scale-105"
                        : "opacity-40 hover:opacity-70"
                    }`}
                >
                  <Image
                    src={src}
                    alt={`${alt} - ${i + 1}`}
                    fill
                    className="object-cover"
                    sizes="56px"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
